// =============================================================================
// src/features/analytics/analytics-directory-markup.ts - Tabelas operacionais.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import {
  type OmarAnalyticsDirectoryMention,
  type OmarAnalyticsInfluencer,
  type OmarAnalyticsSentiment,
  type OmarAnalyticsSite,
} from './analytics-data';
import { OMAR_ANALYTICS_SENTIMENT_LABELS } from './analytics-labels';
import { analyticsLiveSource, getAnalyticsInfluencers, getAnalyticsSites } from './analytics-live-store';

/** Renderiza a planilha de influenciadores com linha expansivel. */
export function buildAnalyticsInfluencersViewMarkup(): string {
  return `<section class="u-analytics-module" data-analytics-module-view="influencers" aria-labelledby="analyticsInfluencersTitle" hidden>
    <div class="u-analytics-panel"><div class="u-analytics-panel-head"><div><span>Influenciadores</span><h2 id="analyticsInfluencersTitle">Perfis que movem a conversa</h2></div><b>${getAnalyticsInfluencers().length}</b></div>
    <div class="u-analytics-table is-directory">${analyticsInfluencerHeaderMarkup()}${getAnalyticsInfluencers().map(analyticsInfluencerRowGroupMarkup).join('')}</div></div>
  </section>`;
}

/** Renderiza a planilha de sites com linha expansivel. */
export function buildAnalyticsSitesViewMarkup(): string {
  return `<section class="u-analytics-module" data-analytics-module-view="sites" aria-labelledby="analyticsSitesTitle" hidden>
    <div class="u-analytics-panel"><div class="u-analytics-panel-head"><div><span>Sites</span><h2 id="analyticsSitesTitle">Portais e dominios monitorados</h2></div><b>${getAnalyticsSites().length}</b></div>
    <div class="u-analytics-table is-directory">${analyticsSiteHeaderMarkup()}${getAnalyticsSites().map(analyticsSiteRowGroupMarkup).join('')}</div></div>
  </section>`;
}

// No live (021) as tabelas trocam as colunas demonstrativas que só rendiam
// travessão (sentimento/desempenho/alcance — backlog F3) por dado REAL da
// janela 30d: engajamento e, nos sites, o escopo editorial do veículo.
function analyticsInfluencerHeaderMarkup(): string {
  if (analyticsLiveSource() === 'live') {
    return `<div class="u-analytics-table-row is-head is-influencer is-live"><span>Perfil</span><span>Mencoes</span><span>Engajamento</span><span>Plataforma</span><span>Seguidores</span><span>Voz</span></div>`;
  }
  return `<div class="u-analytics-table-row is-head is-influencer"><span>Perfil</span><span>Mencoes</span><span>Sentimento</span><span>Plataforma</span><span>Seguidores</span><span>Desempenho</span><span>Alcance</span><span>Voz</span></div>`;
}

function analyticsInfluencerRowGroupMarkup(influencer: OmarAnalyticsInfluencer): string {
  if (influencer.expandedMentions.length === 0) return analyticsInfluencerRowMarkup(influencer);
  return `${analyticsInfluencerRowMarkup(influencer)}${analyticsExpandedInfluencerRowMarkup(influencer)}`;
}

function analyticsInfluencerRowMarkup(influencer: OmarAnalyticsInfluencer): string {
  const sentimentAttr = influencer.sentiment ? ` data-analytics-row-sentiment="${influencer.sentiment}"` : '';
  const expandAttr = influencer.expandedMentions.length > 0 ? ` data-analytics-expand-row="${influencer.influencerId}" aria-expanded="false"` : '';
  if (analyticsLiveSource() === 'live') {
    return `<button class="u-analytics-table-row is-influencer is-live" type="button"${expandAttr} data-analytics-influencer-row data-analytics-row-platform="${escapeHtml(influencer.platform)}"${sentimentAttr}>
    <strong>${escapeHtml(influencer.influencerName)}</strong><span>${influencer.mentions}</span><span>${escapeHtml(influencer.engagementLabel ?? '—')}</span>
    <span>${escapeHtml(influencer.platform)}</span><span>${escapeHtml(influencer.followers)}</span><span>${escapeHtml(influencer.voiceShare)}</span>
  </button>`;
  }
  return `<button class="u-analytics-table-row is-influencer" type="button"${expandAttr} data-analytics-influencer-row data-analytics-row-platform="${escapeHtml(influencer.platform)}"${sentimentAttr}>
    <strong>${escapeHtml(influencer.influencerName)}</strong><span>${influencer.mentions}</span>${analyticsSentimentBarMarkup(influencer.sentiment, influencer.sentimentBars)}
    <span>${escapeHtml(influencer.platform)}</span><span>${escapeHtml(influencer.followers)}</span><span>${escapeHtml(influencer.performance)}</span><span>${escapeHtml(influencer.reach)}</span><span>${escapeHtml(influencer.voiceShare)}</span>
  </button>`;
}

function analyticsSiteHeaderMarkup(): string {
  if (analyticsLiveSource() === 'live') {
    return `<div class="u-analytics-table-row is-head is-site is-live"><span>Site</span><span>Escopo</span><span>Mencoes</span><span>Engajamento</span></div>`;
  }
  return `<div class="u-analytics-table-row is-head is-site"><span>Site</span><span>Tipo</span><span>Mencoes</span><span>Sentimento</span><span>Visitas</span><span>Desempenho</span><span>Alcance</span></div>`;
}

function analyticsSiteRowGroupMarkup(site: OmarAnalyticsSite): string {
  if (site.expandedMentions.length === 0) return analyticsSiteRowMarkup(site);
  return `${analyticsSiteRowMarkup(site)}${analyticsExpandedSiteRowMarkup(site)}`;
}

function analyticsSiteRowMarkup(site: OmarAnalyticsSite): string {
  const sentimentAttr = site.sentiment ? ` data-analytics-row-sentiment="${site.sentiment}"` : '';
  const expandAttr = site.expandedMentions.length > 0 ? ` data-analytics-expand-row="${site.siteId}" aria-expanded="false"` : '';
  if (analyticsLiveSource() === 'live') {
    return `<button class="u-analytics-table-row is-site is-live" type="button"${expandAttr} data-analytics-site-row data-analytics-site-kind="${escapeHtml(site.siteKind)}"${sentimentAttr}>
    <strong>${escapeHtml(site.siteName)}</strong><span>${escapeHtml(site.scopeLabel ?? 'Nacional (nao cadastrado)')}</span><span>${site.mentions}</span><span>${escapeHtml(site.engagementLabel ?? '—')}</span>
  </button>`;
  }
  return `<button class="u-analytics-table-row is-site" type="button"${expandAttr} data-analytics-site-row data-analytics-site-kind="${escapeHtml(site.siteKind)}"${sentimentAttr}>
    <strong>${escapeHtml(site.siteName)}</strong><span>${escapeHtml(site.siteKind)}</span><span>${site.mentions}</span>${analyticsSentimentBarMarkup(site.sentiment, site.sentimentBars)}
    <span>${escapeHtml(site.visits)}</span><span>${escapeHtml(site.performance)}</span><span>${escapeHtml(site.reach)}</span>
  </button>`;
}

/** Sem sentimento na coleta viva (F3.1): celula vira travessao em vez de barras ficticias. */
function analyticsSentimentBarMarkup(sentiment: OmarAnalyticsSentiment | null, bars: readonly [number, number, number] | null): string {
  if (!sentiment || !bars) return '<span class="u-analytics-sentiment-cell is-empty"><em>&mdash;</em></span>';
  return `<span class="u-analytics-sentiment-cell"><i class="is-negative" style="width:${bars[0]}%"></i><i class="is-neutral" style="width:${bars[1]}%"></i><i class="is-positive" style="width:${bars[2]}%"></i><em class="is-${sentiment}">${OMAR_ANALYTICS_SENTIMENT_LABELS[sentiment]}</em></span>`;
}

function analyticsExpandedInfluencerRowMarkup(influencer: OmarAnalyticsInfluencer): string {
  return analyticsExpandedDirectoryRowMarkup({
    rowId: influencer.influencerId,
    profileTitle: influencer.influencerName,
    profileMeta: `${influencer.handle} - ${influencer.location}`,
    profileStat: `${influencer.followers} seguidores`,
    profileSummary: influencer.lastMention,
    mentions: influencer.expandedMentions,
  });
}

function analyticsExpandedSiteRowMarkup(site: OmarAnalyticsSite): string {
  return analyticsExpandedDirectoryRowMarkup({
    rowId: site.siteId,
    profileTitle: site.siteName,
    profileMeta: site.siteKind,
    profileStat: `${site.visits} visitas mensais`,
    profileSummary: site.lastHeadline,
    mentions: site.expandedMentions,
  });
}

interface AnalyticsExpandedDirectoryRow {
  readonly rowId: string;
  readonly profileTitle: string;
  readonly profileMeta: string;
  readonly profileStat: string;
  readonly profileSummary: string;
  readonly mentions: readonly OmarAnalyticsDirectoryMention[];
}

function analyticsExpandedDirectoryRowMarkup(expandedRow: AnalyticsExpandedDirectoryRow): string {
  return `<div class="u-analytics-expanded-row" data-analytics-expanded-row="${escapeHtml(expandedRow.rowId)}" hidden>
    ${analyticsExpandedProfileMarkup(expandedRow)}<div class="u-analytics-expanded-mentions">${expandedRow.mentions.map(analyticsDirectoryMentionMarkup).join('')}</div>
  </div>`;
}

function analyticsExpandedProfileMarkup(expandedRow: AnalyticsExpandedDirectoryRow): string {
  return `<aside class="u-analytics-expanded-profile"><span>${escapeHtml(expandedRow.profileMeta)}</span><strong>${escapeHtml(expandedRow.profileTitle)}</strong><small>${escapeHtml(expandedRow.profileStat)}</small><p>${escapeHtml(expandedRow.profileSummary)}</p></aside>`;
}

function analyticsDirectoryMentionMarkup(mention: OmarAnalyticsDirectoryMention): string {
  return `<article class="u-analytics-directory-mention"><header><span>${escapeHtml(mention.dateLabel)} - ${escapeHtml(mention.platform)}</span><em class="is-${mention.sentiment}">${OMAR_ANALYTICS_SENTIMENT_LABELS[mention.sentiment]}</em></header>
    <strong>${escapeHtml(mention.title)}</strong><p>${escapeHtml(mention.excerpt)}</p>
    <footer><span>${escapeHtml(mention.score)}</span><span>${escapeHtml(mention.reach)}</span><span>${escapeHtml(mention.emotion)}</span><button type="button" data-analytics-local-action="Mencao adicionada ao relatorio">Adicionar ao relatorio</button></footer></article>`;
}
